/**
 * Manages CSS variables for sticky promo bars (announcement bar and countdown timer).
 * Calculates heights and sets CSS variables for proper sticky positioning offsets.
 */
class PromoBarHeight {
  /**
   * Initialises the Promo bar height calculator.
   */
  constructor() {
    // No early return here so offset logic always works even if elems are not found.
    this.handleResize = this.debounce(this.updateAllHeights.bind(this), 150);
    this.init();
  }

  /**
   * Initialises event listeners and sets initial heights.
   */
  init() {
    this.updateAllHeights();
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Gets the announcement bar element, re-querying to handle dynamic show/hide.
   * @returns {HTMLElement|null} The announcement bar element or null if not found.
   */
  getAnnouncementBarElement() {
    return document.querySelector('.js-announcement-bar');
  }

  /**
   * Gets the countdown timer wrapper element, re-querying to handle dynamic show/hide.
   * @returns {HTMLElement|null} The countdown timer wrapper element or null if not found.
   */
  getCountdownTimerWrapperElement() {
    return document.querySelector('.js-countdown-timer-wrapper');
  }

  /**
   * Gets the height of the announcement bar element.
   * Returns 0 if element doesn't exist or is hidden.
   * @returns {number} The height in pixels.
   */
  getAnnouncementBarHeight() {
    const element = this.getAnnouncementBarElement();
    if (!element || !(element instanceof HTMLElement)) return 0;
    return element.offsetHeight;
  }

  /**
   * Gets the height of the countdown timer wrapper element.
   * Returns 0 if element doesn't exist or is hidden.
   * @returns {number} The height in pixels.
   */
  getCountdownTimerWrapperHeight() {
    const element = this.getCountdownTimerWrapperElement();
    if (!element || !(element instanceof HTMLElement)) return 0;
    return element.offsetHeight;
  }

  /**
   * Sets the CSS variable for announcement bar height.
   */
  setAnnouncementBarHeight() {
    const height = this.getAnnouncementBarHeight();
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }

  /**
   * Sets the CSS variable for countdown timer wrapper height.
   */
  setCountdownTimerWrapperHeight() {
    const height = this.getCountdownTimerWrapperHeight();
    document.documentElement.style.setProperty('--countdown-timer-wrapper-height', `${height}px`);
  }

  /**
   * Sets the CSS variable for total promo bar offset (sum of both heights).
   */
  setTotalOffset() {
    const announcementBarHeight = this.getAnnouncementBarHeight();
    const countdownTimerHeight = this.getCountdownTimerWrapperHeight();
    document.documentElement.style.setProperty(
      '--total-promo-bar-offset',
      `${announcementBarHeight + countdownTimerHeight}px`
    );
  }

  /**
   * Updates all height CSS variables.
   * Re-queries elements to handle dynamic show/hide scenarios.
   */
  updateAllHeights() {
    this.setAnnouncementBarHeight();
    this.setCountdownTimerWrapperHeight();
    this.setTotalOffset();
  }

  /**
   * Creates a debounced version of a function.
   * @param {Function} fn - The function to debounce.
   * @param {number} delay - The delay in milliseconds.
   * @returns {(...args: any[]) => void} The debounced function.
   */
  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Initialise on DOMContentLoaded for safety
document.addEventListener('DOMContentLoaded', () => {
  new PromoBarHeight();
});
